import React, { useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, Gauge } from 'lucide-react';
import { LoadingSpinner } from '../../common';
import { EdiService, formatCents } from '../../../services/billing/edi.service';
import type { EdiCountCents, EdiDashboard as EdiDashboardDto, EdiTotals } from '../../../dtos/billing/edi.dto';
import AssignedToMe from './AssignedToMe';
import { Cell, ErrorNote, SimulatedBadge, errorText } from './ediUi';

export type EdiView = 'dashboard' | 'batches' | 'exceptions' | 'deadlines' | 'enrollment';

interface EdiDashboardProps {
  onNavigate: (view: EdiView, id?: string) => void;
}

const TILES: Array<{ key: keyof EdiTotals; label: string }> = [
  { key: 'billed', label: 'Billed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'paid', label: 'Paid' },
  { key: 'denied', label: 'Denied' },
  { key: 'inFlight', label: 'In flight' },
];

const Tile: React.FC<{ label: string; current: EdiCountCents; previous: EdiCountCents }> = ({ label, current, previous }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`edi-tile-${label}`}>
    <p className="text-xs font-medium uppercase text-slate-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-slate-900">{formatCents(current.cents)}</p>
    <p className="text-sm text-slate-600">{current.count} {current.count === 1 ? 'claim' : 'claims'}</p>
    <p className="mt-1 text-xs text-slate-500">Last month: {formatCents(previous.cents)} · {previous.count}</p>
  </div>
);

/** RPT-01 agency billing dashboard (Phase 22 R1). */
const EdiDashboard: React.FC<EdiDashboardProps> = ({ onNavigate }) => {
  const [data, setData] = useState<EdiDashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load the billing dashboard')); });
    return () => { cancelled = true; };
  }, []);

  if (error) return <div className="p-3"><ErrorNote message={error} /></div>;
  if (!data) return <LoadingSpinner label="Loading billing dashboard" />;

  return (
    <div className="space-y-4 p-3 sm:p-4" aria-label="Billing dashboard">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-slate-800">Pelora Billing{data.period.month ? ` · ${data.period.month}` : ''}</h2>
        {data.simulated && <SimulatedBadge />}
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700" data-testid="edi-send-mode">
          Send mode: {data.sendMode === 'auto' ? 'Automatic' : 'Review before send'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {TILES.map((t) => <Tile key={t.key} label={t.label} current={data.totals[t.key]} previous={data.lastMonth[t.key]} />)}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="flex items-center gap-1 text-xs font-medium uppercase text-slate-500"><Gauge size={14} /> Touchless rate</p>
          <p className="mt-1 text-lg font-semibold text-slate-900" data-testid="edi-touchless">
            {data.touchlessRate === null ? '—' : `${Math.round(data.touchlessRate * 100)}%`}
          </p>
          <p className="text-xs text-slate-500">Claims paid with no manual step</p>
        </div>
        <button type="button" onClick={() => onNavigate('exceptions')} className="rounded-lg border border-slate-200 bg-white p-3 text-left hover:bg-slate-50">
          <p className="flex items-center gap-1 text-xs font-medium uppercase text-slate-500"><AlertTriangle size={14} /> Open exceptions</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">{data.openExceptions}</p>
          <p className="text-xs text-slate-600 underline">Open the worklist</p>
        </button>
        <button type="button" onClick={() => onNavigate('deadlines')} className="rounded-lg border border-slate-200 bg-white p-3 text-left hover:bg-slate-50">
          <p className="flex items-center gap-1 text-xs font-medium uppercase text-slate-500"><CalendarClock size={14} /> Deadlines within 15 days</p>
          <p className="mt-1 text-lg font-semibold text-slate-900">{data.deadlinesSoon}</p>
          <p className="text-xs text-slate-600 underline">Open the deadline board</p>
        </button>
      </div>

      <AssignedToMe onOpen={(id) => onNavigate('exceptions', id)} />

      <section aria-label="A/R aging by payer" className="rounded-lg border border-slate-200 bg-white p-3">
        <h3 className="mb-2 text-sm font-semibold text-slate-800">A/R aging by payer</h3>
        {data.aging.length === 0 ? (
          <p className="text-sm text-slate-500">No open receivables.</p>
        ) : (
          <div className="text-sm">
            <div className="hidden grid-cols-5 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
              <span>Payer</span><span className="text-right">0–30</span><span className="text-right">31–60</span><span className="text-right">61–90</span><span className="text-right">90+</span>
            </div>
            {data.aging.map((row) => (
              <div key={row.payerId || row.payerName} className="grid grid-cols-2 gap-1 border-b border-slate-100 py-2 sm:grid-cols-5 sm:gap-2">
                <Cell label="Payer" className="col-span-2 font-medium sm:col-span-1">{row.payerName}</Cell>
                <Cell label="0–30" className="sm:text-right">{formatCents(row.b0_30)}</Cell>
                <Cell label="31–60" className="sm:text-right">{formatCents(row.b31_60)}</Cell>
                <Cell label="61–90" className="sm:text-right">{formatCents(row.b61_90)}</Cell>
                <Cell label="90+" className={`sm:text-right ${row.b90 > 0 ? 'text-red-700' : ''}`}>{formatCents(row.b90)}</Cell>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default EdiDashboard;
